import { Inject, Injectable, signal } from '@angular/core';
import {
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult, RedirectRequest } from '@azure/msal-browser';
import { BehaviorSubject, catchError, filter, from, map, Observable, of, switchMap, tap } from 'rxjs';
import { Profile } from '../models/profile';
import { environment } from '../../environments/environment';
import { RDataService } from './rdata.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private profileSource = new BehaviorSubject<Profile | null>(null);
  profile = this.profileSource.asObservable();

  hasAccess = signal<boolean>(true);

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private rDataService: RDataService
  ) {}

  login(logInRequest?: RedirectRequest) {
    const request = {
      ...this.msalGuardConfig?.authRequest,
      ...logInRequest,
    };

    this.authService.instance.handleRedirectPromise().then((result) => {
      this.authService.loginRedirect(request as RedirectRequest);
      if (result?.account) {
        this.authService.instance.setActiveAccount(result.account);
      }
    });
  }

  logout() {
    this.unsetProfile();
    this.authService.logout();
  }

  unsetProfile(): void {
    this.authService.instance.clearCache();
  }

  isLoggedIn(): boolean {
    const accounts = this.authService.instance.getAllAccounts();
    return accounts.length > 0;
  }

  getProfile(): Profile | null {
    const activeProfile = this.profileSource.getValue();

    if (activeProfile) {
      return activeProfile;
    }
    
    const profileInProgress = this.getProfileIdentity();

    if (!!profileInProgress) {
      this.profileSource.next(profileInProgress);

      return profileInProgress;
    }

    return null;
  }

  getProfileAsObservable(): Observable<Profile | null> {
    const activeProfile = this.getProfile();

    if (!!activeProfile) {
      return of(activeProfile);
    }

    const profileInProgress = this.getProfileIdentity();
    if (!!profileInProgress) {
      return this.rDataService.getUserDetails()
      .pipe(
        map((details) => {
          profileInProgress.details = details;

          return profileInProgress;
        }),
        tap(() => {
          this.profileSource.next(profileInProgress);
        })
      )

    }

    return of(null);
  }

  private getProfileAccount(): AccountInfo | null {
    const allAccounts = this.authService.instance.getAllAccounts();

    if (allAccounts && allAccounts.length > 0) {
      const activeAccount = allAccounts[0];

      this.authService.instance.setActiveAccount(activeAccount);

      return activeAccount;
    }

    return null;
  }

  private getProfileIdentity(): Profile | null {
    const activeAccount = this.getProfileAccount();

    if (activeAccount) {
      const claims: { [key: string]: any } =
        this.getIdTokenClaims(activeAccount);

      const profile: Profile = {
        name: claims['name'],
        email: claims['emails'][0],
      };

      return profile;
    }

    return null;
  }

  private getIdTokenClaims(accountToUse: AccountInfo) {
    if (!!accountToUse || this.isLoggedIn()) {
      const activeAccount = !!accountToUse
        ? accountToUse
        : this.authService.instance.getActiveAccount();
      const claims = activeAccount?.idTokenClaims ?? {};

      return claims;
    }

    return {};
  }

    //Get all accounts from MSAL, iterate through until we find valid account to make active, otherwise emit empty observable.
    checkTokenStatus(): Observable<AuthenticationResult> {
      const accounts = this.authService.instance.getAllAccounts();
      //emit each account as an individual observable item
      return from(accounts).pipe(
        switchMap((account) => {
          //handle each account. set the active account and try to acquire token
          this.authService.instance.setActiveAccount(account);
          return this.authService.acquireTokenSilent({ 
            scopes: [`${environment.azureADB2C.apiPermissionRootUrl}/api.read`],
          }).pipe(
            map((result: AuthenticationResult) => {
              //Token acquired successfully, return the result.
              return result;
            }),
            catchError((error) => {
              //Token acquisition failed, continue with the next account
              if(accounts.indexOf(account) === accounts.length - 1){
                //All accounts failed, throw an error to be handled
                throw error;
              }
  
              //If not the last account, return an empty observable to continue
              //Since the error has been caught and the empty observable is returned, the observable chain does not terminate.
              //The next account is then emitted
              return new Observable<AuthenticationResult>();
            })
          )
        })
      )
    }
}
